import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { FaArrowRight, FaChartLine, FaWallet } from "react-icons/fa";
import { MdPayments } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import api from "../../api";

const currency = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 2,
});

const formatCurrency = (value) => currency.format(Number(value) || 0);

const getCurrentFinancialYear = () => {
  const today = new Date();
  return today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
};

const getFinancialYearOptions = () => {
  const currentYear = getCurrentFinancialYear();
  return [currentYear, currentYear - 1];
};

const getFinancialYearLabel = (startYear) => `${startYear}-${String(startYear + 1).slice(-2)}`;

const getMonthLabel = (month) => {
  if (!/^\d{4}-\d{2}$/.test(month)) return month;
  const [year, monthNumber] = month.split("-").map(Number);
  return new Date(year, monthNumber - 1, 1).toLocaleString("en-IN", { month: "short" });
};

const isDateInFinancialYear = (dateValue, financialYearStart) => {
  if (!dateValue) return false;
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return false;

  const start = new Date(financialYearStart, 3, 1);
  const end = new Date(financialYearStart + 1, 3, 1);
  return date >= start && date < end;
};

export default function RevenueOverview() {
  const [loading, setLoading] = useState(true);
  const [revenueRows, setRevenueRows] = useState([]);
  const [error, setError] = useState("");
  const [financialYear, setFinancialYear] = useState(getCurrentFinancialYear());
  const navigate = useNavigate();

  useEffect(() => {
    let active = true;

    const fetchRevenue = async () => {
      try {
        const schoolsResponse = await api.get("/super_admin/school/list");
        const schools = schoolsResponse.data?.data || [];
        const results = await Promise.all(
          schools.map(async (school) => {
            try {
              const response = await api.get(
                `/super_admin/subscriptions/list?school_id=${school.id}`
              );
              const subscriptions = response.data?.data || [];
              const paidSubscriptions = subscriptions.filter(
                (subscription) => String(subscription.status).toLowerCase() === "paid"
              );
              const total = paidSubscriptions.reduce(
                (sum, subscription) => sum + (Number(subscription.amount) || 0),
                0
              );

              return {
                id: school.id,
                name: school.name,
                total,
                payments: paidSubscriptions.length,
                subscriptions: paidSubscriptions,
              };
            } catch (requestError) {
              console.error(`Fetch revenue error for school ${school.id}:`, requestError);
              return { id: school.id, name: school.name, total: 0, payments: 0, subscriptions: [] };
            }
          })
        );

        if (active) {
          setRevenueRows(results.sort((first, second) => second.total - first.total));
        }
      } catch (requestError) {
        console.error("Fetch revenue overview error:", requestError);
        if (active) setError("Unable to load revenue data.");
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchRevenue();
    return () => {
      active = false;
    };
  }, []);

  const totals = useMemo(() => {
    const rowsForYear = revenueRows.map((row) => {
      const subscriptions = row.subscriptions.filter((subscription) =>
        isDateInFinancialYear(
          subscription.paid_date || subscription.created_at,
          financialYear
        )
      );
      return {
        ...row,
        subscriptions,
        total: subscriptions.reduce((sum, subscription) => sum + (Number(subscription.amount) || 0), 0),
        payments: subscriptions.length,
      };
    }).sort((first, second) => second.total - first.total);

    const monthlyRevenue = rowsForYear.reduce((sum, row) => sum + row.total, 0);
    const totalPayments = rowsForYear.reduce((sum, row) => sum + row.payments, 0);
    const highest = rowsForYear[0]?.total || 0;
    const monthlyCollections = {};

    rowsForYear.forEach((row) => {
      row.subscriptions.forEach((subscription) => {
        const month = subscription.month || "Unknown";
        if (!monthlyCollections[month]) {
          monthlyCollections[month] = { month, total: 0, payments: 0 };
        }
        monthlyCollections[month].total += Number(subscription.amount) || 0;
        monthlyCollections[month].payments += 1;
      });
    });

    const monthlyRows = Object.values(monthlyCollections).sort((first, second) =>
      String(first.month).localeCompare(String(second.month))
    );

    return { rowsForYear, monthlyRows, monthlyRevenue, totalRevenue: monthlyRevenue, totalPayments, highest };
  }, [revenueRows, financialYear]);

  const chartMax = Math.max(...totals.monthlyRows.map((row) => row.total), 1);

  return (
    <Box sx={{ minHeight: "100vh" }}>
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ color: "#102a43", fontWeight: 900, fontSize: { xs: 22, md: 28 } }}>
          Revenue Overview
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
          <Typography sx={{ color: "#607086", mt: 0.5, fontWeight: 600 }}>
            Track paid subscription collections by financial year
          </Typography>
          <TextField
            select
            size="small"
            label="Financial Year"
            value={financialYear}
            onChange={(event) => setFinancialYear(Number(event.target.value))}
            sx={{ minWidth: 165, background: "rgba(255,255,255,0.88)" }}
          >
            {getFinancialYearOptions().map((year) => (
              <MenuItem key={year} value={year}>
                FY {getFinancialYearLabel(year)}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
          <CircularProgress sx={{ color: "#0891b2" }} />
        </Box>
      ) : (
        <>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" }, gap: 2, mb: 3 }}>
            {[
              { label: `FY ${getFinancialYearLabel(financialYear)} Collection`, value: formatCurrency(totals.monthlyRevenue), icon: <MdPayments />, color: "#0f766e" },
              { label: "Total Revenue", value: formatCurrency(totals.totalRevenue), icon: <FaWallet />, color: "#f59e0b" },
              { label: "Paid Transactions", value: totals.totalPayments, icon: <FaWallet />, color: "#0f766e" },
            ].map((metric) => (
              <Paper key={metric.label} sx={{ p: 2, display: "flex", alignItems: "center", gap: 1.5, borderRadius: 2, border: "1px solid rgba(78,102,135,0.14)", borderTop: `3px solid ${metric.color}`, boxShadow: "0 12px 28px rgba(16,42,67,0.08)", background: "rgba(255,255,255,0.9)" }}>
                <Box sx={{ width: 46, height: 46, display: "grid", placeItems: "center", borderRadius: 1.5, color: metric.color, background: `${metric.color}18`, fontSize: 22 }}>
                  {metric.icon}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ color: "#607086", fontWeight: 700, fontSize: 13 }}>{metric.label}</Typography>
                  <Typography sx={{ color: "#172033", fontWeight: 900, fontSize: 22, overflowWrap: "anywhere" }}>{metric.value}</Typography>
                </Box>
              </Paper>
            ))}
          </Box>

          <Card sx={{ p: { xs: 2, md: 3 }, mb: 3, borderRadius: 2, border: "1px solid rgba(78,102,135,0.14)", boxShadow: "0 16px 40px rgba(16,42,67,0.1)", background: "linear-gradient(145deg, rgba(255,255,255,0.98), rgba(239,250,250,0.92))" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <FaChartLine color="#2563eb" />
              <Typography sx={{ color: "#102a43", fontWeight: 900, fontSize: 18 }}>FY {getFinancialYearLabel(financialYear)} Monthly Collections</Typography>
            </Box>
            {totals.monthlyRows.length ? (
              <Box sx={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(totals.monthlyRows.length, 12)}, minmax(55px, 1fr))`, alignItems: "end", gap: { xs: 1, sm: 2 }, height: 260, overflowX: "auto", pb: 1 }}>
                {totals.monthlyRows.map((row, index) => (
                  <Box key={row.month} sx={{ minWidth: 55, height: "100%", display: "flex", flexDirection: "column", justifyContent: "flex-end", alignItems: "center", gap: 1 }}>
                    <Typography sx={{ color: "#102a43", fontSize: 11, fontWeight: 800, whiteSpace: "nowrap" }}>{formatCurrency(row.total)}</Typography>
                    <Box sx={{ width: "65%", minHeight: row.total ? 10 : 3, height: `${Math.max((row.total / chartMax) * 78, row.total ? 8 : 2)}%`, borderRadius: "6px 6px 2px 2px", background: index % 2 ? "linear-gradient(180deg, #0891b2, #2563eb)" : "linear-gradient(180deg, #2563eb, #123a5a)", transformOrigin: "bottom", animation: "revenueBarRise .65s ease both", animationDelay: `${index * 90}ms`, "@keyframes revenueBarRise": { from: { transform: "scaleY(0)" }, to: { transform: "scaleY(1)" } } }} />
                    <Typography sx={{ color: "#607086", fontSize: 11, fontWeight: 800, textAlign: "center", whiteSpace: "nowrap" }}>{getMonthLabel(row.month)}</Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography color="text.secondary" sx={{ py: 5, textAlign: "center" }}>No paid revenue found.</Typography>
            )}
          </Card>

          <TableContainer component={Paper} sx={{ borderRadius: 2, overflowX: "auto", border: "1px solid rgba(78,102,135,0.14)", boxShadow: "0 14px 34px rgba(16,42,67,0.08)" }}>
            <Table sx={{ minWidth: 620 }}>
              <TableHead sx={{ bgcolor: "#123a5a" }}>
                <TableRow>
                  <TableCell sx={{ color: "#fff", fontWeight: 800 }}>School</TableCell>
                  <TableCell sx={{ color: "#fff", fontWeight: 800 }}>Paid Transactions</TableCell>
                  <TableCell align="right" sx={{ color: "#fff", fontWeight: 800 }}>FY Collection</TableCell>
                  <TableCell align="right" sx={{ color: "#fff", fontWeight: 800 }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {totals.rowsForYear.map((row, index) => (
                  <TableRow key={row.id} hover sx={{ backgroundColor: index % 2 ? "#f7fafc" : "#fff" }}>
                    <TableCell sx={{ fontWeight: 800, color: "#172033" }}>{row.name}</TableCell>
                    <TableCell>{row.payments}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 900, color: "#0f766e" }}>{formatCurrency(row.total)}</TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="contained"
                        endIcon={<FaArrowRight />}
                        onClick={() => navigate(`/super_admin/revenue/${row.id}`)}
                        sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 800, background: "linear-gradient(135deg, #2563eb, #0891b2)", boxShadow: "none" }}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                  {!totals.rowsForYear.length && <TableRow><TableCell colSpan={4} align="center" sx={{ py: 5 }}>No collections found for FY {getFinancialYearLabel(financialYear)}.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
}
